import React, { useState, useEffect } from "react";

interface DatePickerProps {
    isOpen: boolean;
    onClose: () => void;
    departureDate: string;
    returnDate: string;
    onDatesChange: (departure: string, returnDate: string) => void;
    tripType: "round-trip" | "one-way" | "multi-city";
}

const DatePicker: React.FC<DatePickerProps> = ({
    isOpen,
    onClose,
    departureDate,
    returnDate,
    onDatesChange,
    tripType,
}) => {
    const [selectedDeparture, setSelectedDeparture] = useState<Date | null>(
        null
    );
    const [selectedReturn, setSelectedReturn] = useState<Date | null>(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());

    useEffect(() => {
        // Parse existing dates if they exist
        if (departureDate) {
            const parts = departureDate.split("-");
            if (parts.length === 3) {
                const monthNames = [
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "May",
                    "Jun",
                    "Jul",
                    "Aug",
                    "Sep",
                    "Oct",
                    "Nov",
                    "Dec",
                ];
                const monthIndex = monthNames.indexOf(parts[1]);
                if (monthIndex !== -1) {
                    const date = new Date(
                        parseInt(parts[2]),
                        monthIndex,
                        parseInt(parts[0])
                    );
                    setSelectedDeparture(date);
                    setCurrentMonth(
                        new Date(date.getFullYear(), date.getMonth(), 1)
                    );
                }
            }
        }
        if (returnDate && tripType !== "one-way") {
            const parts = returnDate.split("-");
            if (parts.length === 3) {
                const monthNames = [
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "May",
                    "Jun",
                    "Jul",
                    "Aug",
                    "Sep",
                    "Oct",
                    "Nov",
                    "Dec",
                ];
                const monthIndex = monthNames.indexOf(parts[1]);
                if (monthIndex !== -1) {
                    const date = new Date(
                        parseInt(parts[2]),
                        monthIndex,
                        parseInt(parts[0])
                    );
                    setSelectedReturn(date);
                }
            }
        }
    }, [departureDate, returnDate, tripType]);

    if (!isOpen) return null;

    const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];

    const shortMonthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];

    const getDaysInMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const formatDate = (date: Date) => {
        const day = date.getDate();
        const month = shortMonthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const handleDateClick = (day: number, month: Date) => {
        const clickedDate = new Date(
            month.getFullYear(),
            month.getMonth(),
            day
        );

        if (
            !selectedDeparture ||
            (selectedDeparture && selectedReturn) ||
            tripType === "one-way"
        ) {
            // Start new selection
            setSelectedDeparture(clickedDate);
            setSelectedReturn(null);
        } else if (!selectedReturn) {
            // Select return date (only happens for round-trip or multi-city)
            if (clickedDate < selectedDeparture) {
                // If clicked date is before departure, swap them
                setSelectedReturn(selectedDeparture);
                setSelectedDeparture(clickedDate);
            } else {
                setSelectedReturn(clickedDate);
            }
        }
    };

    const isDateInRange = (day: number, month: Date) => {
        if (!selectedDeparture || !selectedReturn) return false;
        const date = new Date(month.getFullYear(), month.getMonth(), day);
        return date >= selectedDeparture && date <= selectedReturn;
    };

    const isDateSelected = (day: number, month: Date) => {
        const date = new Date(month.getFullYear(), month.getMonth(), day);
        return (
            (selectedDeparture &&
                date.getTime() === selectedDeparture.getTime()) ||
            (selectedReturn && date.getTime() === selectedReturn.getTime())
        );
    };

    const handleContinue = () => {
        if (selectedDeparture) {
            const depDate = formatDate(selectedDeparture);
            const retDate = selectedReturn ? formatDate(selectedReturn) : "";
            onDatesChange(depDate, retDate);
            onClose();
        }
    };

    const nextMonth = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + 1,
        1
    );

    const renderCalendar = (month: Date) => {
        const daysInMonth = getDaysInMonth(month);
        const firstDay = getFirstDayOfMonth(month);
        const days = [];

        // Add empty cells for days before month starts
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="h-9"></div>);
        }

        // Add days of month
        for (let day = 1; day <= daysInMonth; day++) {
            const isSelected = isDateSelected(day, month);
            const isInRange = isDateInRange(day, month);
            const date = new Date(month.getFullYear(), month.getMonth(), day);
            const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));

            // Determine if this is the start or end of the range
            const isStart =
                selectedDeparture &&
                date.getTime() === selectedDeparture.getTime();
            const isEnd =
                selectedReturn && date.getTime() === selectedReturn.getTime();

            days.push(
                <div key={day} className="relative h-9">
                    {/* Background for range */}
                    {isInRange && !isSelected && (
                        <div className="absolute inset-0 bg-blue-100" />
                    )}
                    {/* Rounded corners for start/end */}
                    {isStart && selectedReturn && (
                        <div className="absolute inset-0 bg-blue-100 rounded-l-lg" />
                    )}
                    {isEnd && selectedDeparture && (
                        <div className="absolute inset-0 bg-blue-100 rounded-r-lg" />
                    )}

                    <button
                        onClick={() => !isPast && handleDateClick(day, month)}
                        disabled={isPast}
                        className={`
                            relative w-full h-full text-sm font-medium transition-colors z-10
                            ${
                                isPast
                                    ? "text-gray-300 cursor-not-allowed"
                                    : "hover:bg-gray-200 hover:bg-opacity-50"
                            }
                            ${isSelected ? "text-white rounded-lg" : ""}
                            ${isInRange && !isSelected ? "text-blue-900" : ""}
                            ${
                                !isSelected && !isInRange && !isPast
                                    ? "text-gray-700"
                                    : ""
                            }
                        `}
                        style={isSelected ? { backgroundColor: "#0047ba" } : {}}
                    >
                        {day}
                    </button>
                </div>
            );
        }

        return days;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex lg:items-center lg:justify-center z-50">
            <div className="bg-white lg:rounded-2xl w-full h-full lg:h-auto lg:mx-4 lg:max-w-[710px] lg:max-h-[485px] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center px-8 pt-6 pb-6">
                    <h2 className="text-xl font-normal text-gray-900">
                        Pick your dates
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <div className="border-b border-gray-200"></div>

                {/* Calendars - Scrollable container */}
                <div className="lg:flex landscape:flex gap-8 px-8 py-6 overflow-y-auto flex-1">
                    {/* First Month */}
                    <div className="flex-1 relative">
                        <div className="flex items-center mb-4">
                            {/* Left arrow - only show if not current month */}
                            {(currentMonth.getMonth() !==
                                new Date().getMonth() ||
                                currentMonth.getFullYear() !==
                                    new Date().getFullYear()) && (
                                <button
                                    onClick={() =>
                                        setCurrentMonth(
                                            new Date(
                                                currentMonth.getFullYear(),
                                                currentMonth.getMonth() - 1,
                                                1
                                            )
                                        )
                                    }
                                    className="absolute left-0 p-1 hover:bg-gray-100 rounded"
                                >
                                    <svg
                                        className="w-5 h-5 text-gray-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 19l-7-7 7-7"
                                        />
                                    </svg>
                                </button>
                            )}
                            <h3 className="text-lg font-semibold text-gray-900 w-full text-center">
                                {monthNames[currentMonth.getMonth()]}{" "}
                                {currentMonth.getFullYear()}
                            </h3>
                            {/* Right arrow for mobile */}
                            <button
                                onClick={() =>
                                    setCurrentMonth(
                                        new Date(
                                            currentMonth.getFullYear(),
                                            currentMonth.getMonth() + 1,
                                            1
                                        )
                                    )
                                }
                                className="lg:hidden landscape:hidden absolute right-0 p-1 hover:bg-gray-100 rounded"
                            >
                                <svg
                                    className="w-5 h-5 text-gray-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                    />
                                </svg>
                            </button>
                        </div>

                        {/* Day headers */}
                        <div className="grid grid-cols-7 gap-0 mb-1">
                            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(
                                (day) => (
                                    <div
                                        key={day}
                                        className="text-center text-sm font-medium text-gray-600 h-7 flex items-center justify-center"
                                    >
                                        {day}
                                    </div>
                                )
                            )}
                        </div>

                        {/* Days */}
                        <div className="grid grid-cols-7 gap-0">
                            {renderCalendar(currentMonth)}
                        </div>
                    </div>

                    {/* Second Month - Show on desktop and landscape */}
                    <div className="hidden lg:block landscape:block flex-1 relative">
                        <div className="flex items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 w-full text-center">
                                {monthNames[nextMonth.getMonth()]}{" "}
                                {nextMonth.getFullYear()}
                            </h3>
                            <button
                                onClick={() =>
                                    setCurrentMonth(
                                        new Date(
                                            currentMonth.getFullYear(),
                                            currentMonth.getMonth() + 1,
                                            1
                                        )
                                    )
                                }
                                className="absolute right-0 p-1 hover:bg-gray-100 rounded"
                            >
                                <svg
                                    className="w-5 h-5 text-gray-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                    />
                                </svg>
                            </button>
                        </div>

                        {/* Day headers */}
                        <div className="grid grid-cols-7 gap-0 mb-1">
                            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(
                                (day) => (
                                    <div
                                        key={day}
                                        className="text-center text-sm font-medium text-gray-600 h-7 flex items-center justify-center"
                                    >
                                        {day}
                                    </div>
                                )
                            )}
                        </div>

                        {/* Days */}
                        <div className="grid grid-cols-7 gap-0">
                            {renderCalendar(nextMonth)}
                        </div>
                    </div>
                </div>

                <div className="border-b border-gray-200"></div>

                {/* Footer - Fixed at bottom */}
                <div className="px-8 py-6 bg-white">
                    {/* Desktop layout */}
                    <div className="hidden lg:flex items-center justify-between">
                        <div className="flex gap-12">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">
                                    Departure
                                </p>
                                <p className="text-lg font-semibold text-gray-900">
                                    {selectedDeparture
                                        ? formatDate(selectedDeparture)
                                        : "Select date"}
                                </p>
                            </div>
                            {tripType !== "one-way" && (
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">
                                        Return
                                    </p>
                                    <p className="text-lg font-semibold text-gray-900">
                                        {selectedReturn
                                            ? formatDate(selectedReturn)
                                            : "Select date"}
                                    </p>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={handleContinue}
                            disabled={
                                !selectedDeparture ||
                                (tripType !== "one-way" && !selectedReturn)
                            }
                            className={`px-8 py-3 rounded-lg font-semibold transition-colors ${
                                selectedDeparture &&
                                (tripType === "one-way" || selectedReturn)
                                    ? "text-white"
                                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                            }`}
                            style={
                                selectedDeparture &&
                                (tripType === "one-way" || selectedReturn)
                                    ? { backgroundColor: "#0047ba" }
                                    : {}
                            }
                        >
                            Continue
                        </button>
                    </div>

                    {/* Mobile layout - stacked */}
                    <div className="lg:hidden space-y-4">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">
                                Departure
                            </p>
                            <p className="text-lg font-semibold text-gray-900">
                                {selectedDeparture
                                    ? formatDate(selectedDeparture)
                                    : "Select date"}
                            </p>
                        </div>
                        {tripType !== "one-way" && (
                            <div>
                                <p className="text-sm text-gray-600 mb-1">
                                    Return
                                </p>
                                <p className="text-lg font-semibold text-gray-900">
                                    {selectedReturn
                                        ? formatDate(selectedReturn)
                                        : "Select date"}
                                </p>
                            </div>
                        )}
                        <button
                            onClick={handleContinue}
                            disabled={
                                !selectedDeparture ||
                                (tripType !== "one-way" && !selectedReturn)
                            }
                            className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                                selectedDeparture &&
                                (tripType === "one-way" || selectedReturn)
                                    ? "text-white"
                                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                            }`}
                            style={
                                selectedDeparture &&
                                (tripType === "one-way" || selectedReturn)
                                    ? { backgroundColor: "#0047ba" }
                                    : {}
                            }
                        >
                            Continue
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DatePicker;
